import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, Chrome, RefreshCw } from 'lucide-react';
import { isBrowserSupported, requiredFeaturesSupported } from '../utils/browserCompatCheck';

const BrowserCompatibilityWarning: React.FC = () => {
  const [showWarning, setShowWarning] = useState(false);
  
  useEffect(() => {
    // Check browser compatibility
    const isSupported = isBrowserSupported() && requiredFeaturesSupported();
    
    // Only show warning if not supported
    setShowWarning(!isSupported);
    
    // Store in session storage so we don't show it on every page
    if (!isSupported && !sessionStorage.getItem('browser-warning-dismissed')) {
      setShowWarning(true);
    }
  }, []);
  
  const dismissWarning = () => {
    setShowWarning(false);
    // Store in session storage so we don't show it again during this session
    sessionStorage.setItem('browser-warning-dismissed', 'true');
  };
  
  if (!showWarning) {
    return null;
  }
  
  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:bottom-4 md:w-96 bg-amber-50 border border-amber-200 rounded-lg shadow-lg p-4 z-50">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-semibold text-amber-800">Browser Compatibility Warning</h3>
            <button 
              onClick={dismissWarning} 
              className="text-amber-500 hover:text-amber-700"
              aria-label="Dismiss warning"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-amber-700 text-sm mb-3">
            Your browser might not support all features of this website. For the best experience, please use a modern browser.
          </p>
          <div className="flex items-center justify-between">
            <a 
              href="https://www.google.com/chrome/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-amber-700 hover:text-amber-900 text-sm"
            >
              <Chrome className="h-4 w-4" />
              <span>Get Chrome</span>
            </a>
            <button 
              onClick={() => window.location.reload()} 
              className="flex items-center gap-1 text-amber-700 hover:text-amber-900 text-sm"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh Page</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrowserCompatibilityWarning;